// Right-edge sidepanel that embeds AddAi Chat via the published SDK
// (https://chat.add.ai/embed.js). The panel itself stays mounted across
// open/close so the iframe doesn't reload — closing just slides it off
// screen. Chat is mounted lazily on the first open so we don't pay the
// iframe boot cost until the user actually wants it.
//
// Configuring the chat origin (dev / staging / prod) is done via the
// VITE_ADDAI_CHAT_ORIGIN env var. Defaults to the production origin.
//
// Ported from BOHSA CRM's chat integration — same workspace-locked embed
// model (?workspace=<slug>&lockWorkspace=1, standalone auth), just pointed
// at the PTS workspace instead of BOHSA's.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useChatPanel } from './ChatPanelContext';

const DEFAULT_CHAT_ORIGIN = 'https://chat.add.ai';
// Tracey's entity_studio_entities.id in the `pts` workspace (project
// syhzpqqvrplaqdipcymw) — confirmed live via task-pts-context-design.md
// §3.1. Not a secret (it's an opaque row id, useless without the operator's
// own chat.add.ai session), but overridable in case the workspace's Tracey
// entity is ever recreated with a new id.
const DEFAULT_TRACEY_ENTITY_ID = 'e1c186d5-f8ab-49c0-a6f5-10a82dadeef5';
// Docked panel — width is user-resizable (left-edge drag handle) and persisted.
const MIN_WIDTH = 320;
const MAX_WIDTH = 760;
const DEFAULT_WIDTH = 420;
const WIDTH_KEY = 'addai_chat_width';
const clampWidth = (w) => Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w));

// Cached promise so reopening the panel doesn't keep injecting <script>s.
// Lives at module scope on purpose — same SDK instance for the whole app.
let sdkPromise = null;
const loadAddAiSdk = (origin) => {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.AddAiChat) return Promise.resolve(window.AddAiChat);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${origin.replace(/\/+$/, '')}/embed.js`;
    script.async = true;
    script.onload = () => {
      if (window.AddAiChat) resolve(window.AddAiChat);
      else reject(new Error('AddAi Chat SDK loaded but window.AddAiChat is missing'));
    };
    script.onerror = () => {
      sdkPromise = null; // allow a retry on next open
      reject(new Error(`Failed to load AddAi Chat SDK from ${script.src}`));
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
};

export const ChatPanel = () => {
  const { isOpen, close, pageContext, customerLock } = useChatPanel();
  const containerRef = useRef(null);
  const chatRef = useRef(null);
  // Tracks the last customer-instance lock we actually fired, so we only
  // re-fire on a genuine "panel opened" or "customer changed while open"
  // transition — not on every render while the panel stays open on the
  // same customer.
  const lastLockFiredRef = useRef({ open: false, customerId: null });
  // Track first-open so we don't mount chat until the user actually wants it
  // — but once mounted we leave it alive across subsequent open/close cycles.
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // chatRef is a ref so it can't trigger a re-render; track mount readiness in
  // state so the "Loading chat…" placeholder actually disappears once chat is up.
  const [mounted, setMounted] = useState(false);

  // Resizable, persisted panel width.
  const [width, setWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(WIDTH_KEY), 10);
      return Number.isFinite(v) ? clampWidth(v) : DEFAULT_WIDTH;
    } catch {
      return DEFAULT_WIDTH;
    }
  });
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      /* ignore storage failures */
    }
  }, [width]);

  // Drag the left edge to resize. The panel is docked to the right, so its
  // width is the distance from the cursor to the right edge of the viewport.
  const startResize = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    const onMove = (ev) => setWidth(clampWidth(window.innerWidth - ev.clientX));
    const onUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const chatOrigin = (import.meta.env.VITE_ADDAI_CHAT_ORIGIN || DEFAULT_CHAT_ORIGIN).replace(/\/+$/, '');
  // Lock the embed to the PTS workspace and hide the in-chat workspace
  // switcher — this CRM is always the PTS workspace and users can't change it.
  const chatWorkspace = import.meta.env.VITE_ADDAI_CHAT_WORKSPACE || 'pts';

  // First open — kick off SDK load + mount.
  useEffect(() => {
    if (!isOpen) return;
    if (hasOpenedOnce) return;
    setHasOpenedOnce(true);
  }, [isOpen, hasOpenedOnce]);

  // Mount the chat instance once we've decided to. Tear it down only on
  // component unmount, not on every close, so reopening is instant and
  // any in-progress draft / scroll position survives.
  useEffect(() => {
    if (!hasOpenedOnce) return;
    if (!containerRef.current) return;
    if (chatRef.current) return;
    let cancelled = false;
    setLoadError(null);
    loadAddAiSdk(chatOrigin)
      .then((SDK) => {
        if (cancelled) return;
        if (!containerRef.current) return;
        if (chatRef.current) return;
        try {
          chatRef.current = SDK.mount({
            container: containerRef.current,
            chatOrigin,
            title: 'AddAi Chat',
            // Boot params forwarded onto the chat iframe URL: force the PTS
            // workspace and tell the chat UI to hide its workspace switcher so
            // it can't be changed from inside the CRM.
            params: {
              workspace: chatWorkspace,
              lockWorkspace: 1,
            },
            // Initial page context (customer / trace / pension / search the
            // operator was looking at when the panel first mounted). Kept in
            // sync afterward by the setContext effect below. Same v2 envelope
            // AiTables posts via addai:context — see ChatIframeSidebar.jsx.
            context: pageContext || null,
            onNavigate: (target) => {
              // Recipients clicking a context chip inside chat get sent
              // back into the CRM. Same-origin URLs use router-style
              // navigation; external URLs open in a new tab so the chat
              // panel doesn't get unloaded.
              if (!target?.url) return;
              try {
                const u = new URL(target.url, window.location.origin);
                if (u.origin === window.location.origin) {
                  window.location.assign(u.pathname + u.search + u.hash);
                } else {
                  window.open(u.toString(), '_blank', 'noopener');
                }
              } catch {
                window.open(target.url, '_blank', 'noopener');
              }
            },
          });
          setMounted(true);
        } catch (err) {
          console.error('[ChatPanel] mount failed:', err);
          setLoadError(err.message || 'Failed to start chat');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[ChatPanel] SDK load failed:', err);
        setLoadError(err.message || 'Failed to load chat');
      });
    return () => { cancelled = true; };
  }, [hasOpenedOnce, chatOrigin, chatWorkspace]);

  // Live context updates — push a fresh context to the mounted chat instance
  // whenever the operator navigates to a different customer / trace / pension
  // / search (pageContext comes from ChatPanelContext's usePageContext, set
  // by the page component). Mirrors AiTables' ChatIframeSidebar effect that
  // re-fires postContext() on every route change. No-ops until the SDK
  // instance exists (mounted === true); `setContext` itself queues if the
  // iframe hasn't finished its ready handshake yet.
  useEffect(() => {
    if (!mounted || !chatRef.current) return;
    chatRef.current.setContext(pageContext || null);
  }, [mounted, pageContext]);

  // Send a raw message straight to the mounted chat iframe. The published
  // embed.js SDK only exposes setContext/setAuth/signOut/destroy today —
  // there's no public wrapper yet for addai:open-customer-instance (a new
  // message type the addai-chat side is adding, see
  // task-pts-context-design.md §3.3). We reuse the SDK instance's own
  // internal `_post`, which already does the ready-handshake queueing and
  // origin-scoping that setContext relies on, rather than re-implementing
  // iframe lookup + postMessage from scratch. This is a deliberate,
  // documented workaround for the SDK gap, not a change to the message
  // contract itself — the type/shape/origin are exactly what the receiver
  // expects.
  const sendToChatSdk = useCallback((message) => {
    const chat = chatRef.current;
    if (!chat || typeof chat._post !== 'function') {
      console.warn('[ChatPanel] chat SDK not ready, dropping message', message?.type);
      return;
    }
    chat._post(message);
  }, []);

  // Lock the panel to the current customer's Tracey instance — but ONLY
  // while the panel is open. Firing this on page view/navigation would mint
  // an entity instance (auth user + docflows notebook + MCP credentials,
  // see task-pts-context-design.md §2.1) for every customer an operator so
  // much as scrolls past; the owner was explicit that instances should only
  // be created for customers someone actually opens the chat on. So this
  // fires on two transitions only: the panel going from closed to open
  // while a customer context exists, or the customer changing while the
  // panel is already open. It does NOT fire on every render while open on
  // the same customer.
  useEffect(() => {
    if (!isOpen || !mounted || !customerLock?.customerId) {
      if (!isOpen) lastLockFiredRef.current = { open: false, customerId: null };
      return;
    }

    const prev = lastLockFiredRef.current;
    const justOpened = !prev.open;
    const customerChanged = prev.customerId !== customerLock.customerId;

    if (justOpened || customerChanged) {
      const traceyEntityId =
        import.meta.env.VITE_ADDAI_TRACEY_ENTITY_ID || DEFAULT_TRACEY_ENTITY_ID;
      sendToChatSdk({
        type: 'addai:open-customer-instance',
        parentEntityId: traceyEntityId,
        customerId: customerLock.customerId,
        customerName: customerLock.customerName,
        customerMetadata: customerLock.metadata || {},
        // Resume, don't mint. Opening the panel on a customer should land on
        // the conversation the operator was last having with them — every
        // open used to create another session and bury the last one behind
        // the Sessions pill. Chat falls back to creating one when there's
        // nothing to resume; `true` here would force a fresh session every
        // time the panel is opened.
        newSession: false
      });
    }

    lastLockFiredRef.current = { open: true, customerId: customerLock.customerId };
  }, [isOpen, mounted, customerLock, sendToChatSdk]);

  // Destroy on full unmount only.
  useEffect(() => () => {
    if (chatRef.current) {
      try { chatRef.current.destroy(); } catch { /* noop */ }
      chatRef.current = null;
    }
  }, []);

  // Esc closes when open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  const onRetry = useCallback(() => {
    sdkPromise = null;
    setLoadError(null);
    setMounted(false);
    setHasOpenedOnce(false);
    // useEffect re-fires on next render because hasOpenedOnce flips back.
    requestAnimationFrame(() => setHasOpenedOnce(true));
  }, []);

  return (
    // Docked, in-layout panel beside <main> (below the header). Opening it
    // PUSHES the main content over instead of overlaying it; it stays mounted
    // at width 0 when closed so the iframe keeps running. There's no panel
    // chrome — the CRM header's chat icon toggles it open/closed (Esc closes).
    <aside
      aria-label='AddAi Chat'
      aria-hidden={!isOpen}
      className='relative h-full shrink-0 overflow-hidden bg-white dark:bg-base-800'
      style={{
        width: isOpen ? width : 0,
        transition: dragging ? 'none' : 'width 300ms ease-out',
        borderLeft: isOpen ? '1px solid rgba(0,0,0,0.06)' : '0 solid transparent',
      }}
    >
      {/* Inner shell pinned to the live width so open/close just animates the
          outer clip, while a drag resizes both together. */}
      <div className='relative h-full' style={{ width }}>
        {/* Left-edge drag handle to resize the panel; width is persisted. */}
        <div
          role='separator'
          aria-orientation='vertical'
          aria-label='Resize chat'
          onMouseDown={startResize}
          title='Drag to resize'
          className='absolute left-0 top-0 z-20 h-full w-1.5 cursor-col-resize hover:bg-brand-500/30 transition-colors'
        />

        {/* Container the SDK injects its iframe into. */}
        <div ref={containerRef} className='absolute inset-0' />

        {hasOpenedOnce && !mounted && !loadError && (
          <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
            <div className='flex items-center gap-2 text-base-500 dark:text-base-400 text-sm'>
              <Loader2 size={16} className='animate-spin' />
              <span>Loading chat…</span>
            </div>
          </div>
        )}

        {loadError && (
          <div className='absolute inset-0 flex items-center justify-center p-6'>
            <div className='max-w-sm text-center'>
              <div className='inline-flex h-10 w-10 rounded-full bg-warning-100 text-warning-700 items-center justify-center mb-3'>
                <AlertCircle size={20} />
              </div>
              <p className='text-sm font-semibold text-base-800 dark:text-base-100'>Couldn&apos;t load chat</p>
              <p className='text-xs text-base-500 dark:text-base-400 mt-1 break-words'>{loadError}</p>
              <button
                type='button'
                onClick={onRetry}
                className='mt-4 px-3 h-8 text-xs font-semibold rounded-md text-white bg-brand-500 hover:bg-brand-600 transition-colors'
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* While dragging, this overlay captures mouse events so the chat iframe
          doesn't swallow them and the resize cursor stays consistent. */}
      {dragging && <div className='fixed inset-0 z-[60] cursor-col-resize' />}
    </aside>
  );
};

export default ChatPanel;
